import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useProducts } from '/@/hooks/recommendation';
import { getCropConfig } from '/@/store/private-data/recommendations/selectors';
import { getActiveRecommendation } from '/@/store/recommendations/selectors';
import { getSessionBrands } from '/@/store/session/selectors';
import type { Field } from '/@/types/field';
import type { Hybrid } from './hybrid-card';
import type { HybridBrand } from './hybrid-lists';

/**
 * Hybrids ranked for the field's current crop config.
 *
 * Queries `useProducts` directly rather than going through `useRecommendationEditData`: that
 * hook owns the defaulting cascade, and one of its effects rewrites `cropConfig.environment`
 * on mount — a second copy here would discard the environment picked on the previous step.
 */
export const useHybrids = ({
    field,
    crop = 'corn',
    plantingDate,
}: {
    field?: Field;
    crop?: 'corn' | 'soy';
    /** Ranks for another date than the stored one, to preview a planting-date change. */
    plantingDate?: string;
}): { featuredProducts: Hybrid[]; otherProducts: Hybrid[]; brands: HybridBrand[]; isLoading: boolean } => {
    const recommendation = useSelector(getActiveRecommendation);
    const cropConfig = getCropConfig(recommendation, crop);
    const sessionBrands = useSelector(getSessionBrands);
    // Every brand of the crop the app instance offers, not just the first. `sessionBrands[crop]`
    // is mostly competitor brands carrying an empty `codename` (they exist for head-to-head), and
    // only a codename can be queried — so `[0].codename` is usually `''`, which left the query
    // permanently disabled and the step showing "no hybrids".
    //
    // The list is returned as well as joined into the query: the step tabs on the brands it asked
    // for, so a brand the ranking came back empty for still gets a tab that says so.
    const brands = useMemo(
        () => (sessionBrands?.[crop] || []).filter((brand: HybridBrand) => brand.codename),
        [sessionBrands, crop]
    );
    const brandCodenames = useMemo(() => brands.map((brand: HybridBrand) => brand.codename).join(','), [brands]);

    const productsQuery = useProducts(
        // `useProducts` declares these as strings, but its callers all pass a field's numeric
        // coordinates and environment; they only ever end up in a query string.
        {
            lat: field?.lat,
            lng: field?.lng,
            crop: cropConfig?.crop,
            plantingDate: plantingDate ?? cropConfig?.plantingDate,
            // `useProducts` names this param `brandCodename`; the cast below would otherwise
            // hide a mismatch here and silently null the SWR key.
            brandCodename: brandCodenames,
            environment: cropConfig?.environment,
        } as unknown as Parameters<typeof useProducts>[0],
        { enabled: !!brandCodenames && !!cropConfig?.environment }
    );

    return {
        brands,
        featuredProducts: productsQuery.data?.featuredProducts || [],
        otherProducts: productsQuery.data?.otherProducts || [],
        isLoading: productsQuery.isLoading || (productsQuery.isValidating && !productsQuery.data),
    };
};
